// import axios from "axios";
import api from "../api/api";

// const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// const api = axios.create({
//     baseURL: API_BASE_URL,
//     timeout: 30000,
//     headers: {
//         "Content-Type": "application/json",
//     },
// });

// API service for Resources Management
const adminResourcesService = {
  // ========== CATEGORIES ==========

  /**
   * Get all categories with group counts
   * @returns {Promise<Object>} Categories list
   */
  getCategories: async () => {
    const response = await api.get("/admin/resources/categories");
    return response;
  },

  /**
   * Get category by ID
   * @param {string} id - Category ID
   * @returns {Promise<Object>} Category data
   */
  getCategoryById: async (id) => {
    const response = await api.get(`/admin/resources/categories/${id}`);
    return response;
  },

  /**
   * Create a new category
   * @param {Object} data - Category data { name, order?, isActive? }
   * @returns {Promise<Object>} Created category
   */
  createCategory: async (data) => {
    const response = await api.post("/admin/resources/categories", data);
    return response;
  },

  /**
   * Update a category
   * @param {string} id - Category ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Updated category
   */
  updateCategory: async (id, data) => {
    const response = await api.patch(`/admin/resources/categories/${id}`, data);
    return response;
  },

  /**
   * Delete a category
   * @param {string} id - Category ID
   * @returns {Promise<Object>} Delete result
   */
  deleteCategory: async (id) => {
    const response = await api.delete(`/admin/resources/categories/${id}`);
    return response;
  },

  /**
   * Reorder categories
   * @param {Array<string>} orderedIds - Ordered category IDs
   * @returns {Promise<Object>} Reorder result
   */
  reorderCategories: async (orderedIds) => {
    const response = await api.patch("/admin/resources/categories/reorder", {
      orderedIds,
    });
    return response;
  },

  // ========== GROUPS ==========

  /**
   * Get groups by category ID with subgroup counts
   * @param {string} categoryId - Category ID
   * @returns {Promise<Object>} Groups list
   */
  getGroupsByCategory: async (categoryId) => {
    const response = await api.get(
      `/admin/resources/categories/${categoryId}/groups`
    );
    return response;
  },

  /**
   * Get group by ID
   * @param {string} id - Group ID
   * @returns {Promise<Object>} Group data
   */
  getGroupById: async (id) => {
    const response = await api.get(`/admin/resources/groups/${id}`);
    return response;
  },

  /**
   * Create a new group in a category
   * @param {string} categoryId - Category ID
   * @param {Object} data - Group data { name, imageUrl?, order?, isActive? }
   * @returns {Promise<Object>} Created group
   */
  createGroup: async (categoryId, data) => {
    const response = await api.post(
      `/admin/resources/categories/${categoryId}/groups`,
      data
    );
    return response;
  },

  /**
   * Update a group
   * @param {string} id - Group ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Updated group
   */
  updateGroup: async (id, data) => {
    const response = await api.patch(`/admin/resources/groups/${id}`, data);
    return response;
  },

  /**
   * Delete a group
   * @param {string} id - Group ID
   * @returns {Promise<Object>} Delete result
   */
  deleteGroup: async (id) => {
    const response = await api.delete(`/admin/resources/groups/${id}`);
    return response;
  },

  /**
   * Reorder groups within a category
   * @param {string} categoryId - Category ID
   * @param {Array<string>} orderedIds - Ordered group IDs
   * @returns {Promise<Object>} Reorder result
   */
  reorderGroups: async (categoryId, orderedIds) => {
    const response = await api.patch(
      `/admin/resources/categories/${categoryId}/groups/reorder`,
      { orderedIds }
    );
    return response;
  },

  // ========== SUBGROUPS ==========

  /**
   * Get subgroups by group ID with file counts
   * @param {string} groupId - Group ID
   * @returns {Promise<Object>} Subgroups list
   */
  getSubgroupsByGroup: async (groupId) => {
    const response = await api.get(
      `/admin/resources/groups/${groupId}/subgroups`
    );
    return response;
  },

  /**
   * Get subgroup by ID
   * @param {string} id - Subgroup ID
   * @returns {Promise<Object>} Subgroup data
   */
  getSubgroupById: async (id) => {
    const response = await api.get(`/admin/resources/subgroups/${id}`);
    return response;
  },

  /**
   * Create a new subgroup in a group
   * @param {string} groupId - Group ID
   * @param {Object} data - Subgroup data { name, order?, isActive? }
   * @returns {Promise<Object>} Created subgroup
   */
  createSubgroup: async (groupId, data) => {
    const response = await api.post(
      `/admin/resources/groups/${groupId}/subgroups`,
      data
    );
    return response;
  },

  /**
   * Update a subgroup
   * @param {string} id - Subgroup ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Updated subgroup
   */
  updateSubgroup: async (id, data) => {
    const response = await api.patch(`/admin/resources/subgroups/${id}`, data);
    return response;
  },

  /**
   * Delete a subgroup
   * @param {string} id - Subgroup ID
   * @returns {Promise<Object>} Delete result
   */
  deleteSubgroup: async (id) => {
    const response = await api.delete(`/admin/resources/subgroups/${id}`);
    return response;
  },

  /**
   * Reorder subgroups within a group
   * @param {string} groupId - Group ID
   * @param {Array<string>} orderedIds - Ordered subgroup IDs
   * @returns {Promise<Object>} Reorder result
   */
  reorderSubgroups: async (groupId, orderedIds) => {
    const response = await api.patch(
      `/admin/resources/groups/${groupId}/subgroups/reorder`,
      { orderedIds }
    );
    return response;
  },

  // ========== FILES ==========

  /**
   * Get files by subgroup ID
   * @param {string} subgroupId - Subgroup ID
   * @returns {Promise<Object>} Files list
   */
  getFilesBySubgroup: async (subgroupId) => {
    const response = await api.get(
      `/admin/resources/subgroups/${subgroupId}/files`
    );
    return response;
  },

  /**
   * Get file by ID
   * @param {string} id - File ID
   * @returns {Promise<Object>} File data
   */
  getFileById: async (id) => {
    const response = await api.get(`/admin/resources/files/${id}`);
    return response;
  },

  /**
   * Create a file record in a subgroup
   * @param {string} subgroupId - Subgroup ID
   * @param {Object} data - File data { name, fileName?, fileUrl?, fileType?, fileSize?, order?, isActive? }
   * @returns {Promise<Object>} Created file
   */
  createFile: async (subgroupId, data) => {
    const response = await api.post(
      `/admin/resources/subgroups/${subgroupId}/files`,
      data
    );
    return response;
  },

  /**
   * Update a file record
   * @param {string} id - File ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Updated file
   */
  updateFile: async (id, data) => {
    const response = await api.patch(`/admin/resources/files/${id}`, data);
    return response;
  },

  /**
   * Delete a file
   * @param {string} id - File ID
   * @returns {Promise<Object>} Delete result (includes fileUrl for cleanup)
   */
  deleteFile: async (id) => {
    const response = await api.delete(`/admin/resources/files/${id}`);
    return response;
  },

  /**
   * Reorder files within a subgroup
   * @param {string} subgroupId - Subgroup ID
   * @param {Array<string>} orderedIds - Ordered file IDs
   * @returns {Promise<Object>} Reorder result
   */
  reorderFiles: async (subgroupId, orderedIds) => {
    const response = await api.patch(
      `/admin/resources/subgroups/${subgroupId}/files/reorder`,
      { orderedIds }
    );
    return response;
  },

  /**
   * Upload file to Wasabi and create file record
   * @param {string} subgroupId - Subgroup ID
   * @param {File} file - File to upload
   * @param {string} displayName - Optional display name
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Created file with URL
   */
  uploadFile: async (
    subgroupId,
    file,
    displayName = null,
    onProgress = null
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (displayName) formData.append("name", displayName);

    const response = await api.post(
      `/admin/resources/upload-file/${subgroupId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response;
  },

  /**
   * Upload thumbnail image for a group
   * @param {string} groupId - Group ID
   * @param {File} file - Image file to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Updated group with image URL
   */
  uploadThumbnail: async (groupId, file, onProgress = null) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      `/admin/resources/upload-thumbnail/${groupId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response;
  },

  /**
   * Upload thumbnail image for a category
   * @param {string} categoryId - Category ID
   * @param {File} file - Image file to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Updated category with image URL
   */
  uploadCategoryThumbnail: async (categoryId, file, onProgress = null) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      `/admin/resources/upload-category-thumbnail/${categoryId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response;
  },
};

export default adminResourcesService;
